import React, {
  type ChangeEvent,
  type ComponentProps,
  type ComponentType,
  type FocusEvent,
  type ForwardedRef,
  forwardRef,
  type ForwardRefExoticComponent,
  type PropsWithChildren,
  type PropsWithoutRef,
  type RefAttributes,
  type SyntheticEvent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react'
import type { FieldValues, RegisterOptions, Path } from 'react-hook-form'
import { useWatch, useController, useFormState } from 'react-hook-form'
import { useEvent } from '@fs/zion-frontend-friends'
import {
  Checkbox,
  Radio,
  Toggle,
  TextField,
  TextArea,
  MultilineTextField,
  Select,
  Autosuggest,
  ChoiceChips,
} from '@fs/zion-ui'
import {
  DateSuggest,
  PlaceSuggest,
  LanguageSuggest,
  DateSuggestNEXT,
  PlaceSuggestNEXT,
  LanguageSuggestNEXT,
  useStandardSuggestNEXT,
} from '@fs/zion-standards'
import { Autosuggest as AutosuggestNEXT, useAutosuggest as useAutosuggestNEXT } from '@fs/zion-ui-autosuggest'
import isEqual from 'lodash.isequal'
import { useZionFormContext, getByPath } from './Form'

type UseRegisterProps<TFieldValues extends FieldValues = FieldValues> = {
  ref: ForwardedRef<HTMLElement>
  registerOptions?: RegisterOptions<TFieldValues>
  onChange?: (event: ChangeEvent<HTMLElement> | SyntheticEvent) => void
  onBlur?: (event: FocusEvent<HTMLElement>) => void
  name: Path<TFieldValues>
}

function useRegister<TFieldValues extends FieldValues = FieldValues>({
  ref,
  registerOptions,
  onChange,
  onBlur,
  name,
}: UseRegisterProps<TFieldValues>): {
  ref: (domNode: HTMLElement | null) => void
  onChange: (evt: ChangeEvent<HTMLElement> | SyntheticEvent) => void
  onBlur: (evt: FocusEvent<HTMLElement>) => void
  name: string
} {
  const { register } = useZionFormContext<TFieldValues>()

  const {
    ref: registerRef,
    onChange: registerOnChange,
    onBlur: registerOnBlur,
    name: registerName,
  } = register(name, registerOptions as RegisterOptions<TFieldValues, Path<TFieldValues>>)

  // This is the react-hook-form recommended way of sharing ref usage
  const registeredRef = useCallback(
    (domNode: HTMLElement | null) => {
      if (ref) {
        if (typeof ref === 'function') {
          ref(domNode)
        } else {
          ref.current = domNode
        }
      }
      registerRef(domNode)
    },
    [ref, registerRef]
  )

  const combinedOnChange = useCallback(
    (evt: ChangeEvent<HTMLElement> | SyntheticEvent) => {
      registerOnChange?.(evt)
      if (typeof onChange === 'function') {
        onChange(evt)
      }
    },
    [registerOnChange, onChange]
  )

  const combinedOnBlur = useCallback(
    (evt: FocusEvent<HTMLElement>) => {
      registerOnBlur?.(evt)
      if (typeof onBlur === 'function') {
        onBlur(evt)
      }
    },
    [onBlur, registerOnBlur]
  )

  return { ref: registeredRef, onChange: combinedOnChange, onBlur: combinedOnBlur, name: registerName }
}

type UseValidationProps<TFieldValues extends FieldValues = FieldValues> = {
  name: Path<TFieldValues>
  validMessage?: string
  ignoreTouchedForWarnings?: boolean
  warningValidate?: (value: unknown) => string | undefined
}

function useValidation<TFieldValues extends FieldValues = FieldValues>({
  name,
  validMessage,
  ignoreTouchedForWarnings,
  warningValidate,
}: UseValidationProps<TFieldValues>): {
  errorMessage?: string
  validMessage?: string
  warningValidate?: () => string | undefined
} {
  const formContext = useZionFormContext<TFieldValues>()
  const { getValues, control } = formContext
  // Access mode from control._options which is preserved by react-hook-form
  // Fallback to config for backwards compatibility
  // eslint-disable-next-line no-underscore-dangle -- accessing react-hook-form internal property
  const mode = control?._options?.mode || formContext.config?.mode

  const formState = useFormState()
  const { touchedFields, isSubmitted, dirtyFields } = formState
  const [messages, setMessages] = useState<{
    errorMessage?: string
    validMessage?: string
    warningValidate?: () => string | undefined
  }>({})
  const isTouched = getByPath(name, touchedFields) as boolean
  const isDirty = getByPath(name, dirtyFields) as boolean

  let didValidationRunForField = false

  if (mode === 'onSubmit') {
    didValidationRunForField = isSubmitted
  } else if (mode && ['onBlur', 'onTouched', 'all'].includes(mode)) {
    didValidationRunForField = isSubmitted || isTouched
  } else if (mode === 'onChange') {
    // onChange doesn't run validation on blur, so we have to ensure that the field has been changed at least once. This doesn't quite do that - because something could get dirty then undirty, but we have no way of saying "has been changed since the last reset"
    didValidationRunForField = isSubmitted || (isTouched && isDirty)
  }

  useEffect(() => {
    // Recalculate error from formState.errors to get fresh value
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const currentError = getByPath(name, formState.errors) as any
    const runWarningValidateWithValues = warningValidate
      ? () => {
          const value = getByPath(name, getValues())
          return warningValidate(value)
        }
      : undefined
    const errorMessage = currentError?.primaryText ? currentError?.primaryText?.message : currentError?.message
    // We do not want validation messages to show until either the field has been touched (which happens on the first blur from the field) or the form has been submitted. We make an exception where we can show warnings without being touched if the prop is explicitly set.
    setMessages({
      errorMessage: didValidationRunForField ? errorMessage : undefined,
      validMessage: didValidationRunForField ? validMessage : undefined,
      warningValidate: didValidationRunForField || ignoreTouchedForWarnings ? runWarningValidateWithValues : undefined,
    })
  }, [
    formState, // We want to recalculate whenever formState changes
    validMessage,
    warningValidate,
    ignoreTouchedForWarnings,
    didValidationRunForField,
    getValues,
    name,
  ])
  return messages
}

type FormFieldProps = {
  name: string
  warningValidate?: (value: unknown) => string | undefined
  validMessage?: string
  registerOptions?: RegisterOptions
  defaultValue?: unknown
  onClear?: (...args: unknown[]) => void
  onReset?: (...args: unknown[]) => void
  ignoreTouchedForWarnings?: boolean
  onChange?: (event: ChangeEvent<HTMLElement> | SyntheticEvent) => void
  onBlur?: (event: FocusEvent<HTMLElement>) => void
  clearable?: boolean
  resettable?: boolean
}

/**
 * Hook up a generic input field to the react-hook-form and zion-form lifecycle. Do not use this if there is a zion component that already supports being used inside of zion-form.
 * @param {ReactComponent} Component - The component to hook into the zion-form lifecycle.
 * @param {string} componentName - optional - only required for TextField special clearable state handling
 * @returns - ForwardedRef wrapped component. Adds the following props:
 * errorMessage, validMessage, warningValidate, (undefined or string)
 * isInForm={true},
 * onChange, onBlur, ref (combined with any existing onChange/onBlur/refs that are passed in)
 */
export function hookUpTextFields<
  TProps extends
    | ComponentProps<typeof TextField>
    | ComponentProps<typeof TextArea>
    | ComponentProps<typeof MultilineTextField>
    | ComponentProps<typeof Select>,
>(
  Component: ComponentType<TProps>,
  componentName?: string
): ForwardRefExoticComponent<PropsWithoutRef<TProps & FormFieldProps> & RefAttributes<HTMLElement>> {
  return forwardRef<HTMLElement, TProps & FormFieldProps>(
    (
      {
        warningValidate,
        validMessage,
        registerOptions,
        defaultValue,
        onClear,
        onReset,
        ignoreTouchedForWarnings,
        onChange,
        onBlur,
        clearable,
        resettable,
        name,
        ...props
      },
      ref
    ) => {
      const componentSpecificProps: {
        resettable?: boolean
        clearable?: boolean
        onClear?: TProps['onClear']
        onReset?: TProps['onReset']
      } = {}

      const { setValue, resetField, getFieldState } = useZionFormContext()
      const watchedValue = useWatch({ name, defaultValue })
      const messages = useValidation({ name, validMessage, ignoreTouchedForWarnings, warningValidate })
      const registerProps = useRegister({ ref, registerOptions, name, onChange, onBlur })

      // TextField is the only component that currently supports clearable and resettable
      if (componentName === 'TextField') {
        if (clearable) {
          componentSpecificProps.onClear = (...args: unknown[]) => {
            setValue(name, '', {
              shouldValidate: true,
              shouldDirty: true,
            })
            onClear?.(...args)
          }
        }
        if (resettable) {
          // Only show reset button when value has changed
          if (getFieldState(name).isDirty) {
            componentSpecificProps.onReset = (...args: unknown[]) => {
              resetField(name)
              onReset?.(...args)
            }
          } else {
            componentSpecificProps.resettable = false
          }
        }
      }

      return (
        <Component
          resettable={resettable}
          clearable={clearable}
          value={watchedValue ?? ''}
          isInForm
          {...(props as ComponentProps<typeof Component>)}
          {...componentSpecificProps}
          {...messages}
          {...registerProps}
        />
      )
    }
  )
}

type BooleanFieldProps = FormFieldProps & {
  value?: string | number | boolean
}

function hookUpBooleanFields<
  TProps extends ComponentProps<typeof Checkbox> | ComponentProps<typeof Radio> | ComponentProps<typeof Toggle>,
>(
  Component: ComponentType<TProps>
): ForwardRefExoticComponent<PropsWithoutRef<TProps & BooleanFieldProps> & RefAttributes<HTMLElement>> {
  return forwardRef<HTMLElement, TProps & BooleanFieldProps>(
    (
      {
        registerOptions,
        defaultValue,
        onChange,
        onBlur,
        // eslint-disable-next-line @typescript-eslint/no-unused-vars -- intentionally destructured to exclude from props spread
        ignoreTouchedForWarnings: _ignoreTouchedForWarnings,
        // eslint-disable-next-line @typescript-eslint/no-unused-vars -- intentionally destructured to exclude from props spread
        warningValidate: _warningValidate,
        // eslint-disable-next-line @typescript-eslint/no-unused-vars -- intentionally destructured to exclude from props spread
        validMessage: _validMessage,
        // eslint-disable-next-line @typescript-eslint/no-unused-vars -- intentionally destructured to exclude from props spread
        onClear: _onClear,
        // eslint-disable-next-line @typescript-eslint/no-unused-vars -- intentionally destructured to exclude from props spread
        onReset: _onReset,
        // eslint-disable-next-line @typescript-eslint/no-unused-vars -- intentionally destructured to exclude from props spread
        clearable: _clearable,
        // eslint-disable-next-line @typescript-eslint/no-unused-vars -- intentionally destructured to exclude from props spread
        resettable: _resettable,
        ...props
      },
      ref
    ) => {
      const { value, name } = props
      let checked = false
      const formValue = useWatch({ name, defaultValue })
      if (Array.isArray(formValue)) {
        if (formValue.includes(value)) {
          // this specific toggle or checkbox is checked
          checked = true
        }
      } else if (formValue) {
        // the checkbox is checked
        checked = true
      }
      const componentSpecificProps: Record<string, unknown> = {
        isInForm: true,
        checked, // this makes the check mark actually appear
      }
      const registerProps = useRegister({ ref, registerOptions, name, onChange, onBlur })

      return (
        <Component {...(props as ComponentProps<typeof Component>)} {...componentSpecificProps} {...registerProps} />
      )
    }
  )
}

type AutosuggestFieldProps = FormFieldProps & {
  onSuggestionSelected?: (suggestion: unknown) => void
  onInputTextChange?: (text: string) => void
  helpMessage?: string
}

// Autosuggest gets its own function because it doesn't need to forward ref and it does a whole bunch of extra useCallbacks that we don't want everything to have to manage. At the time of writing this, Autosuggest and DateSuggest and PlaceSuggest didn't forwardRef. If that is a feature that gets added, we need to forwardRef here, but we must have all 3 components underneath forwarding the ref too.
function hookUpAutosuggest<
  TProps extends
    | ComponentProps<typeof Autosuggest>
    | ComponentProps<typeof DateSuggest>
    | ComponentProps<typeof PlaceSuggest>
    | ComponentProps<typeof LanguageSuggest>,
>(
  Component: ComponentType<TProps>,
  componentName?: string
): ForwardRefExoticComponent<PropsWithoutRef<TProps & AutosuggestFieldProps> & RefAttributes<HTMLElement>> {
  return forwardRef<HTMLElement, TProps & AutosuggestFieldProps>(
    (
      {
        warningValidate,
        validMessage,
        onSuggestionSelected,
        onClear,
        onBlur,
        onChange,
        onInputTextChange,
        ignoreTouchedForWarnings,
        helpMessage,
        ...props
      },
      ref
    ) => {
      const { name } = props as AutosuggestFieldProps // not listed above because we want it to be part of props that we pass down to Component
      const { control } = useZionFormContext()

      // we don't pull the ref out of the field here and pass it on down because on reset rhf will go through and set the values of the fields if it has access to their ref's and we end up with [object Object] in the input visually. The disadvantage of not using this ref is that it won't be able to focus back to the field on error.
      const {
        field: { onChange: registerOnChange, onBlur: registerOnBlur, value },
      } = useController({ name, control })

      const messages = useValidation({
        name,
        validMessage,
        ignoreTouchedForWarnings,
        warningValidate,
      })

      // Autosuggest functions are wrapped in a useCallback so we don't cause unnecessary thrash by redefining every time. This is why they are outside of the if block for the Autosuggest Components.
      const newOnSuggestionSelected = (suggestion: unknown): void => {
        // if our current value and suggestion are equal, don't trigger an update to the form - it will put us in an infinite cycle because changing the value in the form triggers onSuggestionSelected.
        if (!isEqual(value, suggestion)) {
          registerOnChange(suggestion)
        }
        onSuggestionSelected?.(suggestion)
      }

      const newOnClear = useCallback(
        (suggestion: unknown) => {
          registerOnChange(null)
          onClear?.(suggestion)
        },
        [onClear, registerOnChange]
      )

      const newOnInputTextChange = useCallback(
        (text: string) => {
          // we don't want to accidentally call this during a reset of the form because bad things happen if we registerChange during
          // reset with valid and dirty and touched states. The only way I could think of to ensure the user is actually interacting
          // with the input instead of the reset function being called (because rhf's reset function triggers a bunch of side effects
          // that finish after the reset function has already returned) is to verify that we are responding to a user's input event.
          if (!text && window?.event?.type === 'input') {
            registerOnChange(null)
          }
          onInputTextChange?.(text)
        },
        [onInputTextChange, registerOnChange]
      )

      const newOnBlur = useCallback(
        (evt: FocusEvent<HTMLElement>) => {
          registerOnBlur()
          onBlur?.(evt)
        },
        [onBlur, registerOnBlur]
      )

      let componentSpecificProps: Record<string, unknown> = {
        onSuggestionSelected: newOnSuggestionSelected,
        onClear: newOnClear,
        onBlur: newOnBlur,
        onChange,
        onInputTextChange: newOnInputTextChange,
        // control the autosuggest - this is important for when we reset the form and default values. Both value and selectedItem are required currently to control autosuggest
        selectedItem: value,
        value,
      }

      if (
        componentName === 'Autosuggest' ||
        componentName === 'DateSuggest' ||
        componentName === 'PlaceSuggest' ||
        componentName === 'LanguageSuggest'
      ) {
        // we disallow validation on standard suggest.
        // standard suggest doesn't forward ref.
        componentSpecificProps = { ...componentSpecificProps, ...messages, helpMessage, ref }
      } else if (process.env.NODE_ENV === 'development' && (helpMessage || validMessage || warningValidate)) {
        /* eslint-disable-next-line no-console -- Needed for warning consumers about misuse */
        console.warn('Validation is not allowed on FormDateSuggest or FormPlaceSuggest - UX decision made March 2021')
      }

      return <Component {...(props as ComponentProps<typeof Component>)} {...componentSpecificProps} />
    }
  )
}

type StandardSuggestState = {
  userInput: string
  fieldValue: string
  selectedItem: unknown
  standardSuggestSuggestion: { originalText: string | null; standard: unknown }
}

function getInitialStandardSuggestState(value: unknown): StandardSuggestState {
  const baseState: StandardSuggestState = {
    userInput: '',
    fieldValue: '',
    selectedItem: null,
    standardSuggestSuggestion: { originalText: null, standard: null },
  }
  if (!value) return baseState
  if (typeof value === 'string') return { ...baseState, userInput: value, fieldValue: value }
  if (typeof value === 'object' && value !== null && 'primaryText' in value)
    return { ...baseState, selectedItem: value }
  return { ...baseState, ...(value as Partial<StandardSuggestState>) }
}

type SuggestionItem = { primaryText?: string; [key: string]: unknown }

const defaultHandleSelection = (suggestion: SuggestionItem | null, input: string): string =>
  suggestion?.primaryText ?? input

type AutosuggestNEXTFieldProps = {
  handleSelection?: (suggestion: SuggestionItem | null, input: string) => string
  standardize?: boolean | string
  validMessage?: string
  ignoreTouchedForWarnings?: boolean
  warningValidate?: (value: unknown) => string | undefined
}

function hookUpAutosuggestNEXT<
  TProps extends
    | ComponentProps<typeof AutosuggestNEXT>
    | ComponentProps<typeof PlaceSuggestNEXT>
    | ComponentProps<typeof DateSuggestNEXT>
    | ComponentProps<typeof LanguageSuggestNEXT>,
>(
  Component: ComponentType<TProps>,
  { defaultStandardize = false } = {}
): ForwardRefExoticComponent<PropsWithoutRef<TProps & AutosuggestNEXTFieldProps> & RefAttributes<HTMLElement>> {
  return forwardRef<HTMLElement, TProps & AutosuggestNEXTFieldProps>(
    (
      {
        handleSelection = defaultHandleSelection,
        name,
        validMessage,
        ignoreTouchedForWarnings,
        warningValidate,
        onBlur,
        standardize = defaultStandardize, // Allow prop to override default
        ...props
      },
      ref
    ) => {
      const { control } = useZionFormContext()
      const { field: input, fieldState } = useController({ name: name!, control })

      let initialState: unknown = input.value
      if (typeof initialState === 'string') {
        initialState = { userInput: input.value }
      } else if (typeof initialState === 'object' && initialState !== null && 'primaryText' in initialState) {
        initialState = { selectedItem: initialState }
      } else if (standardize) {
        initialState = getInitialStandardSuggestState(input.value)
      }

      const config = standardize
        ? // eslint-disable-next-line react-hooks/rules-of-hooks -- standardize is static and will never change in this context.
          useStandardSuggestNEXT({ initialState, standardize })
        : // eslint-disable-next-line react-hooks/rules-of-hooks, @typescript-eslint/no-explicit-any -- standardize is static and will never change in this context, type mismatch between initialState and hook expectations
          useAutosuggestNEXT({ initialState: initialState as any, handleSelection })
      const { userInput, selectedItem, set } = config
      const handleChange = useEvent(input.onChange)

      const previousValueRef = useRef<{ userInput: string; selectedItem: SuggestionItem | null }>({
        userInput: '',
        selectedItem: null,
      })
      useEffect(() => {
        // Prevent calling the handleChange function too often and causing performance issues
        if (
          userInput === previousValueRef.current?.userInput &&
          (selectedItem as SuggestionItem)?.primaryText === previousValueRef.current?.selectedItem?.primaryText
        ) {
          return
        }

        previousValueRef.current = { userInput, selectedItem: selectedItem as SuggestionItem | null }
        handleChange({
          userInput,
          selectedItem,
          fieldValue: handleSelection(selectedItem as SuggestionItem | null, userInput as string),
        })
      }, [userInput, selectedItem, handleChange, handleSelection])

      const { isDirty } = fieldState
      const wasDirty = useRef(isDirty)
      const previousInputValue = useRef(input.value)

      useEffect(() => {
        // If we changed from dirty to clean, the field was reset
        if (!isDirty && wasDirty.current) {
          set(input.value)
        }
        // Or if setValue() was called externally
        else if (input.value !== previousInputValue.current) {
          let value = input.value
          if (typeof value === 'string') {
            value = { userInput: value }
          } else if (value?.primaryText) {
            value = { selectedItem: value }
          }
          set(value)
        }

        wasDirty.current = isDirty
        previousInputValue.current = input.value
      }, [isDirty, input.value, set])

      const messages = useValidation({
        name: name!,
        validMessage,
        ignoreTouchedForWarnings,
        warningValidate,
      })

      const newOnBlur = useCallback(
        (evt: FocusEvent<HTMLInputElement>) => {
          input.onBlur()
          onBlur?.(evt)
        },
        [input, onBlur]
      )

      return (
        <Component
          {...(props as ComponentProps<typeof Component>)}
          {...(standardize ? { standardize } : {})} // only pass when true
          {...messages}
          onBlur={newOnBlur}
          name={`${input.name}.fieldValue`}
          config={config}
          ref={ref}
        />
      )
    }
  )
}
// TODO onBlur and onChange should really just come from ComponentProps<typeof ChoiceChips>
type ChoiceChipsProps = ComponentProps<typeof ChoiceChips> & {
  defaultValue?: unknown
  onBlur?: (event: FocusEvent<HTMLElement>) => void
  onChange?: (event: ChangeEvent<HTMLElement> | SyntheticEvent) => void
}

// choice chips gets its own special function because it doesn't need to do the work of forwarding ref
function hookUpChoiceChips(Component: typeof ChoiceChips): ComponentType<ChoiceChipsProps> {
  return ({ chips, defaultValue, name, ...props }: ChoiceChipsProps) => {
    const watchedValue = useWatch({ name, defaultValue })
    const { register } = useZionFormContext()

    // ChoiceChips doesn't really give us a way to set the selected item. We need to do that for resetting the form and for default values. Giving a new chips list on every render ensures that it will check for the selected chip in its use effect after each of our renders. Whenever the watchedValue changes, it will update selected.
    const newChips = chips?.map((chip) => {
      if (!chip) return chip
      const { value, ...nisnergs } = chip
      const { onChange, onBlur, ref: chipRef } = register(name)
      return { value, selected: value === watchedValue, onChange, onBlur, chipRef, ...nisnergs }
    })

    return <Component {...props} name={name} isInForm chips={newChips} />
  }
}

export const FormAutosuggest = hookUpAutosuggest(Autosuggest, 'Autosuggest')
export const FormAutosuggestNEXT = hookUpAutosuggestNEXT(AutosuggestNEXT)
export const FormPlaceSuggestNEXT = hookUpAutosuggestNEXT(PlaceSuggestNEXT)
export const FormDateSuggestNEXT = hookUpAutosuggestNEXT(DateSuggestNEXT)
export const FormLanguageSuggestNEXT = hookUpAutosuggestNEXT(LanguageSuggestNEXT)
export const FormDateSuggest = hookUpAutosuggest(DateSuggest, 'DateSuggest')
export const FormPlaceSuggest = hookUpAutosuggest(PlaceSuggest, 'PlaceSuggest')
export const FormLanguageSuggest = hookUpAutosuggest(LanguageSuggest, 'LanguageSuggest')
export const FormCheckbox = hookUpBooleanFields(Checkbox)
export const FormRadio = hookUpBooleanFields(Radio)
export const FormToggle = hookUpBooleanFields(Toggle)
export const FormTextField = hookUpTextFields(TextField, 'TextField')
export const FormTextArea = hookUpTextFields(TextArea)
export const FormMultilineTextField = hookUpTextFields(MultilineTextField)
export const FormSelect = hookUpTextFields(Select)
export const FormChoiceChips = hookUpChoiceChips(ChoiceChips)

// Type-only export for documentation purposes
export type FieldsWithValidationPropsType = {
  /** Force warningValidate to show up before a field is touched or the form is submitted */
  ignoreTouchedForWarnings?: boolean

  /** Ensure that validation and help text do not show up on the field level because validation is displaying in a parent group component */
  validateAtGroupLevel?: boolean
}

// This component exists only for documentation purposes and prop types
export function FieldsWithValidationProps({ ...props }: PropsWithChildren<FieldsWithValidationPropsType>): JSX.Element {
  return <>{props.children}</>
}
